import { useCallback } from 'react'

import { getUserLocale } from '@/common/utils/locale'
import i18n from '@/modules/i18n/i18n'

type Value = number | undefined | null

export const CENTS_IN_DOLLAR = 100

/**
 * Throws errors if minDecimals is greater than maxDecimals or either are negative
 * @returns minDecimals
 */
function validateFractionDigits(maxDecimals: number, minDecimals?: number) {
  minDecimals = minDecimals ?? maxDecimals
  if (minDecimals > maxDecimals)
    throw RangeError('minDecimals must be less than or equal to maxDecimals')
  if (minDecimals < 0 || maxDecimals < 0) throw RangeError('decimalPlaces must be greater than 0')
  return minDecimals
}

/**
 * @returns Function that formats currency value according to region_id
 */
export const useFormatCurrency = (currencyCode?: string) => {
  return useCallback(
    (value: Value) => formatCurrency(value, getUserLocale(), currencyCode),
    [currencyCode]
  )
}

/**
 * Format number into localized currency
 * @param value Number to be formatted as currency (`1000`)
 * @param localeCode Locale code to format (`en-US`). Backend may pass POSIX (`en_US`).
 * @param currencyCode Currency code to format (`USD`)
 * @returns Number formatted as currency string (`$1,000.00`)
 */
export const formatCurrency = (
  value: Value,
  localeCode: string = 'en-US',
  currencyCode: string = 'USD',
  minimumFractionDigits: number = 2,
  maximumFractionDigits: number = 2
) => {
  // Backend stores POSIX-style locales (e.g. `en_US`); Intl.NumberFormat requires BCP 47 (`en-US`).
  const bcp47Locale = localeCode.replace(/_/g, '-')
  return new Intl.NumberFormat(bcp47Locale, {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(value || 0)
}

/**
 * Format number with localization and specified number of decimals
 * @param value the number to format (`1000.230`)
 * @param maxDecimals (default: 0) If the only value specified, all numbers will have this many decimals (`2`)
 * @param minDecimals (default: undefined) If specified, numbers can have fewer decimal places than the max (`0`)
 * @returns Number formatted as string (`1,000.23`)
 */
export function formatNumber(value: Value, maxDecimals: number = 0, minDecimals?: number) {
  minDecimals = validateFractionDigits(maxDecimals, minDecimals)
  return new Intl.NumberFormat(getUserLocale(), {
    minimumFractionDigits: minDecimals,
    maximumFractionDigits: maxDecimals,
  }).format(value || 0)
}

/**
 * Format percent with localization and specified number of decimals
 * @param value the number to format
 * @param maxDecimals (default: 1) If the only value specified, all numbers will have this many decimals
 * @param minDecimals (default: undefined) If specified, numbers can have fewer decimal places than the max
 */
export function formatPercent(value: Value, maxDecimals: number = 1, minDecimals?: number) {
  minDecimals = validateFractionDigits(maxDecimals, minDecimals)
  return new Intl.NumberFormat(getUserLocale(), {
    minimumFractionDigits: minDecimals,
    maximumFractionDigits: maxDecimals,
    style: 'percent',
  }).format(value || 0)
}

// See: https://github.com/unicode-org/cldr/blob/main/common/validity/unit.xml
type Unit = 'hour' | 'kilometer' | 'meter' | 'mile'
type UnitDisplay = Intl.NumberFormatOptions['unitDisplay']

export function formatUnit({
  value,
  unit,
  unitDisplay,
  minDecimals,
  maxDecimals,
}: {
  value: Value
  unit: Unit
  unitDisplay: UnitDisplay
  minDecimals?: number
  maxDecimals?: number
}) {
  if (minDecimals && maxDecimals) validateFractionDigits(maxDecimals, minDecimals)
  return new Intl.NumberFormat(getUserLocale(), {
    style: 'unit',
    unit,
    ...(minDecimals && { minimumFractionDigits: minDecimals }),
    ...(maxDecimals && { maximumFractionDigits: maxDecimals }),
    ...(unitDisplay && { unitDisplay }),
  }).format(value || 0)
}

export const formatCount = (count: number) => formatNumber(count)

/** The smallest average two decimals can write; anything under half of it rounds away to `0`. */
const SMALLEST_SHOWN_AVERAGE = 0.01

/**
 * Write an averaged count - a shape's vehicles, a segment's daily trips.
 *
 * These are averages over hours or days, so they are fractional, but a reader wants a count:
 * `343`, not `343.1`. Decimals appear only below 1, where the whole number would be `0` - the
 * one thing it must not say about something that is genuinely there. Below the floor two
 * decimals can write, that promise needs `<0.01` rather than a rounded `0.00`. A map legend is
 * the other way round: it labels a whole ladder of breaks, so it takes the precision that tells
 * its own bins apart (see `ColorBin.decimals`).
 */
export const formatAverageCount = (count: number) => {
  if (count === 0 || Math.abs(count) >= 1) return formatNumber(count)
  if (Math.abs(count) < SMALLEST_SHOWN_AVERAGE / 2)
    return `${count < 0 ? '>-' : '<'}${formatNumber(SMALLEST_SHOWN_AVERAGE, 2, 2)}`
  return formatNumber(count, 2, 0)
}

export const formatDistance = (count: number) => (count ? formatNumber(count, 2, 0) : 'n/a')

/** Translated distance-unit abbreviation ('km' / 'mi') for a region's measurement system. */
export const getDistanceUnits = (isMetric: boolean | undefined) =>
  isMetric ? i18n.t('common.kilometer_abbr', 'km') : i18n.t('common.miles_abbr', 'mi')
